import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, MapPinned, List, Map as MapIcon } from "lucide-react";
import { Panel, EmptyState } from "@/components/admin/ui";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { usePolling } from "@/hooks/usePolling";
import { LastUpdated } from "@/components/common/LastUpdated";
import { ServiceAreaCard } from "@/components/org/serviceAreas/ServiceAreaCard";
import { ServiceAreaEditorModal } from "@/components/org/serviceAreas/ServiceAreaEditorModal";
import { ServiceAreaHistoryPanel } from "@/components/org/serviceAreas/ServiceAreaHistoryPanel";
import { ServiceAreasCoverageMap } from "@/components/org/serviceAreas/ServiceAreasCoverageMap";

export default function OrgServiceAreas() {
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [editorArea, setEditorArea] = useState(undefined);
  const [historyArea, setHistoryArea] = useState(null);
  const [deleteState, setDeleteState] = useState(null);
  const [view, setView] = useState("list");
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async (silent) => {
    if (!silent) setError("");
    try {
      const { data: resp } = await axios.get(`${API}/org/service-areas`, { withCredentials: true });
      setData(resp);
      setLastUpdated(Date.now());
    } catch (err) {
      if (!silent) setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  usePolling(() => {
    if (editorArea !== undefined || savingId || historyArea || deleteState) return;
    load(true);
  }, 15000);

  if (!data) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="org-service-areas-loading">
        {error || "Loading service areas…"}
      </div>
    );
  }

  const { service_areas: areas, is_manager: isManager, available_teams: availableTeams = [], available_services: availableServices = [] } = data;

  const toggleStatus = async (area) => {
    setSavingId(area.service_area_id);
    try {
      await axios.patch(`${API}/org/service-areas/${area.service_area_id}`, { status: area.status === "Active" ? "Inactive" : "Active" }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const assignTeam = async (areaId, teamId) => {
    if (!teamId) return;
    setSavingId(areaId);
    try {
      await axios.post(`${API}/org/teams/${teamId}/service-areas`, { service_area_id: areaId }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const unassignTeam = async (areaId, teamId) => {
    setSavingId(areaId);
    try {
      await axios.delete(`${API}/org/teams/${teamId}/service-areas/${areaId}`, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const startDelete = async (area) => {
    try {
      const { data: deps } = await axios.get(`${API}/org/service-areas/${area.service_area_id}/dependencies`, { withCredentials: true });
      setDeleteState({ area, ...deps });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    if (!deleteState) return;
    setSavingId(deleteState.area.service_area_id);
    try {
      const { data: result } = await axios.delete(`${API}/org/service-areas/${deleteState.area.service_area_id}`, { withCredentials: true });
      setDeleteState(null);
      if (result.archived) setError(result.reason);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className={view === "map" ? "flex h-[calc(100vh-140px)] flex-col gap-6" : "space-y-6"} data-testid="org-service-areas-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-950 dark:text-white">Service Areas</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Independent geographic territories, each defined by boundary and served by one or more teams.</p>
        </div>
        <div className="flex items-center gap-3">
          <LastUpdated timestamp={lastUpdated} testId="org-service-areas-last-updated" />
          <div className="flex rounded-lg border border-slate-200 p-1 dark:border-white/10">
            <button type="button" onClick={() => setView("list")} data-testid="org-service-areas-view-list" className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold ${view === "list" ? "bg-[#0052FF] text-white" : "text-slate-500 dark:text-slate-400"}`}><List size={13} /> List</button>
            <button type="button" onClick={() => setView("map")} data-testid="org-service-areas-view-map" className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold ${view === "map" ? "bg-[#0052FF] text-white" : "text-slate-500 dark:text-slate-400"}`}><MapIcon size={13} /> Map</button>
          </div>
          {isManager && (
            <button type="button" onClick={() => setEditorArea(null)} data-testid="org-service-area-create-btn" className="inline-flex items-center gap-1.5 rounded-xl bg-[#0052FF] px-4 py-2.5 text-sm font-bold text-white">
              <Plus size={16} /> New Service Area
            </button>
          )}
        </div>
      </div>

      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-service-areas-error">{error}</p>}

      {view === "map" ? (
        <Panel testId="org-service-areas-map-panel" className="flex min-h-0 flex-1 flex-col">
          <ServiceAreasCoverageMap areas={areas} availableTeams={availableTeams} availableServices={availableServices} />
        </Panel>
      ) : (
        <Panel testId="org-service-areas-panel" action={isManager ? undefined : <span className="text-xs font-semibold text-slate-400">View only — showing your assigned areas</span>}>
          {areas.length === 0 ? (
            <EmptyState icon={MapPinned} title={isManager ? "No service areas yet." : "You are not assigned to any service areas yet."} message={isManager ? "Create your first service area to define where your teams operate." : undefined} testId="org-service-areas-empty" />
          ) : (
            <div className="space-y-3" data-testid="org-service-areas-list">
              {areas.map((area) => (
                <ServiceAreaCard
                  key={area.service_area_id}
                  area={area}
                  isManager={isManager}
                  availableTeams={availableTeams}
                  savingId={savingId}
                  onEdit={setEditorArea}
                  onToggleStatus={toggleStatus}
                  onAssignTeam={assignTeam}
                  onUnassignTeam={unassignTeam}
                  onHistory={setHistoryArea}
                  onDelete={startDelete}
                />
              ))}
            </div>
          )}
        </Panel>
      )}

      {editorArea !== undefined && (
        <ServiceAreaEditorModal
          area={editorArea}
          availableServices={availableServices}
          onClose={() => setEditorArea(undefined)}
          onSaved={() => load()}
        />
      )}

      {historyArea && <ServiceAreaHistoryPanel area={historyArea} onClose={() => setHistoryArea(null)} />}

      {deleteState && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-950/60 p-4" data-testid="sa-delete-confirm-overlay">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-[#0B1220]" data-testid="sa-delete-confirm-modal">
            <p className="text-sm font-extrabold text-slate-950 dark:text-white">
              {deleteState.can_delete ? `Permanently delete "${deleteState.area.name}"?` : `"${deleteState.area.name}" can't be permanently deleted`}
            </p>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400" data-testid="sa-delete-confirm-message">
              {deleteState.can_delete
                ? "This service area has no team assignments and will be permanently deleted. This cannot be undone."
                : `This service area is assigned to ${deleteState.team_assignments} team(s), so it can't be permanently deleted. It will be archived (set to Inactive) instead to preserve those historical relationships.`}
            </p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setDeleteState(null)} data-testid="sa-delete-cancel-btn" className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">Cancel</button>
              <button type="button" onClick={confirmDelete} disabled={savingId === deleteState.area.service_area_id} data-testid="sa-delete-confirm-btn" className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">
                {deleteState.can_delete ? "Delete Permanently" : "Archive Instead"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

